import React, { useState, useEffect, useCallback, useRef, useMemo, memo } from 'react';
import { useMsal } from '@azure/msal-react';
import { List } from 'react-window';
import { authenticatedApiCall } from '../../utils/apiClient';
import GridExportModal from '../common/GridExportModal';
import { ListenButton } from '../common/AudioGuide';
import HelpVideoTour from '../Help/HelpVideoTour';
import { hasHelpTour } from '../Help/HelpWithVideoTour';
import AuditLogModal from '../common/AuditLogModal';
import UploadModal from '../common/UploadModal';
import EditableCell from '../common/EditableCell';
import FilterDropdown from '../common/FilterDropdown';
import { UPLOAD_CONFIGS } from '../../config/uploadConfigs';
import { exportToExcel } from '../../utils/exportToExcel';
import JobStatusMonitor from '../ProgressTracking/Piping/JobStatusMonitor';
import useTableFilter from '../../hooks/useTableFilter';
import useTableSort from '../../hooks/useTableSort';
import { useToast } from '../../hooks/useToast';
import usePresence from '../../hooks/usePresence';
import ActiveViewers from '../Matrix/ActiveViewers';
import MtoAnalyticsDashboard from './MtoAnalyticsDashboard';
import MtoFactorsModal from './MtoFactorsModal';
import MtoCompareModal from './MtoCompareModal';
import SendToPurchasingModal from './SendToPurchasingModal';
import useColumnOrder from '../../hooks/useColumnOrder';
import ColumnConfigPanel from '../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../utils/columnLayout';

// Accepted Joint Type values (shared with the Part Rate DB). Editable via a dropdown.
export const JOIN_TYPE_OPTIONS = ['Welded', 'Threaded', 'Threaded+Welded', 'Victaulic', 'PVC', 'HDPE'];

// Default visible columns (others are available via the Columns config).
const DEFAULT_VISIBLE_COLUMNS = [
  'iso_sheet_number', 'part_number', 'part_name', 'description', 'type', 'material', 'size', 'sch', 'qty',
  'supplier', 'engineering_code', 'datasheet',
];

// readOnly columns are server-calculated (Plan 1) — see documentation/MTO_PLAN_1_COLUMNS.md.
const COLUMNS = [
  { key: 'job_number', label: 'Job #', type: 'text', width: 100 },
  { key: 'estimate_number', label: 'Estimate #', type: 'text', width: 100 },
  { key: 'line_number', label: 'Line #', type: 'text', width: 90 },
  { key: 'iso_sheet_number', label: 'ISO Sheet #', type: 'text', width: 110 },
  { key: 'iso_version', label: 'ISO Version', type: 'text', width: 90 },
  { key: 'spool_number', label: 'Spool #', type: 'text', width: 90 },
  { key: 'part_number', label: 'Part #', type: 'text', width: 90, readOnly: true },
  { key: 'part_name', label: 'Part Name', type: 'text', width: 200, readOnly: true },
  { key: 'description', label: 'Description (rest)', type: 'text', width: 200 },
  { key: 'supplier', label: 'Supplier', type: 'text', width: 140 },
  { key: 'engineering_code', label: 'Eng Code', type: 'text', width: 110 },
  { key: 'datasheet', label: 'Specs/Datasheet', type: 'custom', width: 190, readOnly: true },
  { key: 'type', label: 'Type', type: 'text', width: 80 },
  { key: 'material', label: 'Material', type: 'text', width: 120 },
  { key: 'size', label: 'Size', type: 'text', width: 70 },
  { key: 'bore', label: 'Bore', type: 'text', width: 70, readOnly: true },
  { key: 'dia_in', label: 'Dia In', type: 'number', width: 70, readOnly: true },
  { key: 'total_dia_in', label: 'Total Dia In', type: 'number', width: 90, readOnly: true },
  { key: 'sch', label: 'SCH', type: 'text', width: 60 },
  { key: 'sch_modified', label: 'SCH Modified', type: 'text', width: 100 },
  { key: 'rating', label: 'Rating', type: 'text', width: 80 },
  { key: 'end_type', label: 'End Type', type: 'text', width: 80 },
  { key: 'seam_type', label: 'Seam Type', type: 'text', width: 80 },
  { key: 'join_type', label: 'Joint Type', type: 'select', options: JOIN_TYPE_OPTIONS, width: 120 },
  { key: 'qty', label: 'Qty', type: 'number', width: 70 },
  { key: 'purchasing_qty', label: 'Purchasing Qty', type: 'number', width: 110 },
  { key: 'joints', label: 'Joints', type: 'number', width: 70 },
  { key: 'total_joints', label: 'Total Joints', type: 'number', width: 90, readOnly: true },
  { key: 'um', label: 'UM', type: 'text', width: 60 },
  { key: 'system', label: 'System', type: 'text', width: 100 },
  { key: 'subsystem', label: 'Subsystem', type: 'text', width: 100 },
  { key: 'piping_class', label: 'Piping Class', type: 'text', width: 100 },
  { key: 'fluid_code', label: 'Fluid Code', type: 'text', width: 90 },
  { key: 'sov', label: 'SOV', type: 'text', width: 90 },
  { key: 'wbs', label: 'WBS', type: 'text', width: 90 },
  { key: 'length', label: 'Length', type: 'number', width: 80 },
  { key: 'fabrication_shop', label: 'Fab Shop', type: 'text', width: 120 },
  { key: 'priority', label: 'Priority', type: 'text', width: 80 },
  // Material / bore classification (calculated)
  { key: 'mat_modified_for_matbore', label: 'Mat Modified for MatBore', type: 'text', width: 140, readOnly: true },
  { key: 'matbore', label: 'MatBore', type: 'text', width: 90, readOnly: true },
  // Hours (split shop/field by the estimate's shop/field ratio — see Factors panel)
  { key: 'mh_per_ea', label: 'MH per EA', type: 'number', width: 90, readOnly: true },
  { key: 'total_hours', label: 'Total Hours', type: 'number', width: 90, readOnly: true },
  { key: 'total_hours_shop', label: 'Total Hours - Shop', type: 'number', width: 120, readOnly: true },
  { key: 'total_hours_field', label: 'Total Hours - Field', type: 'number', width: 120, readOnly: true },
  // Material cost (unit_price_cad is the editable source; shop/field are allocations)
  { key: 'unit_price_cad', label: 'Unit Price CAD', type: 'number', width: 110 },
  { key: 'unit_price_cad_shop', label: 'Unit Price CAD - Shop', type: 'number', width: 140, readOnly: true },
  { key: 'unit_price_cad_field', label: 'Unit Price CAD - Field', type: 'number', width: 140, readOnly: true },
  { key: 'unit_price_usd_shop', label: 'Unit Price USD - Shop', type: 'number', width: 140, readOnly: true },
  { key: 'unit_price_usd_field', label: 'Unit Price USD - Field', type: 'number', width: 140, readOnly: true },
  { key: 'total_material_cad_shop', label: 'Total Material CAD - Shop', type: 'number', width: 160, readOnly: true },
  { key: 'total_material_cad_field', label: 'Total Material CAD - Field', type: 'number', width: 160, readOnly: true },
  { key: 'total_material_usd_shop', label: 'Total Material USD - Shop', type: 'number', width: 160, readOnly: true },
  { key: 'total_material_usd_field', label: 'Total Material USD - Field', type: 'number', width: 160, readOnly: true },
  // Labour cost (shop/field labour rates are set per estimate in the Factors panel)
  { key: 'labour_rate_cad_shop', label: 'Labour Rate CAD - Shop', type: 'number', width: 150, readOnly: true },
  { key: 'labour_rate_cad_field', label: 'Labour Rate CAD - Field', type: 'number', width: 150, readOnly: true },
  { key: 'labour_rate_usd_shop', label: 'Labour Rate USD - Shop', type: 'number', width: 150, readOnly: true },
  { key: 'labour_rate_usd_field', label: 'Labour Rate USD - Field', type: 'number', width: 150, readOnly: true },
  { key: 'total_labour_cad_shop', label: 'Total Labour CAD - Shop', type: 'number', width: 160, readOnly: true },
  { key: 'total_labour_cad_field', label: 'Total Labour CAD - Field', type: 'number', width: 160, readOnly: true },
  { key: 'total_labour_usd_shop', label: 'Total Labour USD - Shop', type: 'number', width: 160, readOnly: true },
  { key: 'total_labour_usd_field', label: 'Total Labour USD - Field', type: 'number', width: 160, readOnly: true },
  // Grand totals (base + shop/field)
  { key: 'grand_total_cad', label: 'Grand Total CAD', type: 'number', width: 130, readOnly: true },
  { key: 'grand_total_cad_shop', label: 'Grand Total CAD - Shop', type: 'number', width: 150, readOnly: true },
  { key: 'grand_total_cad_field', label: 'Grand Total CAD - Field', type: 'number', width: 150, readOnly: true },
  { key: 'grand_total_usd', label: 'Grand Total USD', type: 'number', width: 130, readOnly: true },
  { key: 'grand_total_usd_shop', label: 'Grand Total USD - Shop', type: 'number', width: 150, readOnly: true },
  { key: 'grand_total_usd_field', label: 'Grand Total USD - Field', type: 'number', width: 150, readOnly: true },
  // Grand totals after factors — the 6 result columns (base + shop + field)
  { key: 'grand_total_cad_after_factors', label: 'Grand Total CAD (After Factors)', type: 'number', width: 190, readOnly: true },
  { key: 'grand_total_cad_after_factors_shop', label: 'Grand Total CAD (After Factors) - Shop', type: 'number', width: 220, readOnly: true },
  { key: 'grand_total_cad_after_factors_field', label: 'Grand Total CAD (After Factors) - Field', type: 'number', width: 220, readOnly: true },
  { key: 'grand_total_usd_after_factors', label: 'Grand Total USD (After Factors)', type: 'number', width: 190, readOnly: true },
  { key: 'grand_total_usd_after_factors_shop', label: 'Grand Total USD (After Factors) - Shop', type: 'number', width: 220, readOnly: true },
  { key: 'grand_total_usd_after_factors_field', label: 'Grand Total USD (After Factors) - Field', type: 'number', width: 220, readOnly: true },
  // Pricing factors (unit-price jobs)
  { key: 'price_cut_to_pipe_factor', label: 'Price Cut to Pipe Factor', type: 'number', width: 150 },
  { key: 'fitting_to_fittings_factor', label: 'Fitting to Fittings Factor', type: 'number', width: 150 },
  { key: 'pipe_to_pipe_factor', label: 'Pipe to Pipe Factor', type: 'number', width: 130 },
  { key: 'pipe_to_pipe_qty', label: 'Pipe to Pipe Qty', type: 'number', width: 130 },
  // Misc / classification
  { key: 'class', label: 'Class', type: 'text', width: 100 },
  { key: 'restrictions', label: 'Restrictions', type: 'text', width: 130 },
  { key: 'support_code', label: 'Support Code', type: 'text', width: 120 },
  { key: 'trim_allowance', label: 'Trim Allowance', type: 'number', width: 100 },
  { key: 'rev', label: 'Rev', type: 'text', width: 70 },
  { key: 'comments', label: 'Comments', type: 'text', width: 150 },
];

// Source + explanation for every MTO column, shown in Help → "All Columns".
// Source keys: 'import' | 'prdb' | 'calc' | 'manual' | 'config' | 'system'.
const COLUMN_HELP = {
  job_number: ['system', 'Job number — carried from the estimate/job context.'],
  estimate_number: ['system', 'Estimate this MTO row belongs to.'],
  line_number: ['import', 'Line tag number from the client MTO.'],
  iso_sheet_number: ['import', 'Primary line identifier (ISO sheet). Drives the tracker push and ISO-churn analytics.'],
  iso_version: ['import', 'Engineering ISO version (e.g. 0, 1, A). Used for the versioned tracker-line model.'],
  spool_number: ['import', 'Spool reference (optional).'],
  part_number: ['prdb', 'Pulled from the Part Rate DB when the row matches.'],
  part_name: ['prdb', 'Part Rate DB unique key: Type – Material – Size – SCH – Rating – End Type – Seam Type.'],
  description: ['import', 'Free-text description — the remaining text after the spec columns.'],
  supplier: ['manual', 'Preferred/awarded supplier for this item (editable).'],
  engineering_code: ['manual', 'Engineering / drawing code for this item (editable).'],
  datasheet: ['prdb', 'Spec/datasheet PDF for this line, scoped to this estimate (a spec uploaded on another estimate does not show here). Click the filename to view; use 📎 to upload a new version — every version is kept in the Part Rate DB history.'],
  type: ['import', 'Part type. One of the 5 Part Rate DB match columns.'],
  material: ['import', 'Material. One of the 5 match columns.'],
  size: ['import', 'Nominal size. Match column; also drives Bore.'],
  bore: ['calc', '≤2″ = SB, ≤24″ = LB, >24″ = XLB.'],
  dia_in: ['prdb', 'Per-each diameter-inches, sourced directly from the Part Rate DB on match.'],
  total_dia_in: ['calc', 'Dia In × Qty.'],
  sch: ['import', 'Schedule (wall thickness). Match column.'],
  sch_modified: ['manual', 'Normalized schedule, retained for review.'],
  rating: ['import', 'Pressure rating. Match column.'],
  end_type: ['import', 'End preparation (informational; not used for Part Rate matching).'],
  seam_type: ['import', 'Seam type (informational; not used for Part Rate matching).'],
  join_type: ['manual', 'Welded / Threaded / Threaded+Welded / Victaulic / PVC / HDPE.'],
  qty: ['import', 'Quantity. Multiplies hours, material and dia-in totals.'],
  purchasing_qty: ['manual', 'Quantity to purchase including waste allowance. Used only by the purchase requisition (falls back to Qty when blank); man-hours and cost totals still use Qty. Editable here and in the Send-to-Purchasing window.'],
  joints: ['import', 'Joints per each.'],
  total_joints: ['calc', 'Joints × Qty.'],
  um: ['import', 'Unit of measure.'],
  system: ['import', 'System classification.'],
  subsystem: ['import', 'Sub-system classification.'],
  piping_class: ['import', 'Piping class / spec.'],
  fluid_code: ['import', 'Fluid type classification.'],
  sov: ['import', 'Schedule of Values reference code.'],
  wbs: ['import', 'Work breakdown structure code.'],
  length: ['import', 'Pipe length.'],
  fabrication_shop: ['manual', 'Fabrication location.'],
  priority: ['manual', 'Priority flag.'],
  mat_modified_for_matbore: ['calc', 'Material family (SS / CS / PVC / HDPE) by ASTM rules; unknown = REVIEW.'],
  matbore: ['calc', 'Family-bore combination (e.g. SS-LB).'],
  mh_per_ea: ['prdb', 'OTTAV man-hours per each, from the Part Rate DB.'],
  total_hours: ['calc', 'Qty × MH per EA (OTTAV basis).'],
  total_hours_shop: ['calc', 'Total Hours × shop ratio.'],
  total_hours_field: ['calc', 'Total Hours × (1 − shop ratio).'],
  unit_price_cad: ['manual', 'Material unit cost (CAD). Fed from the Part Rate DB cost on match; editable.'],
  unit_price_cad_shop: ['calc', 'Unit Price CAD allocated by shop ratio.'],
  unit_price_cad_field: ['calc', 'Unit Price CAD allocated by field ratio.'],
  unit_price_usd_shop: ['calc', 'Unit Price CAD - Shop × frozen FX rate.'],
  unit_price_usd_field: ['calc', 'Unit Price CAD - Field × frozen FX rate.'],
  total_material_cad_shop: ['calc', 'Unit Price CAD × Qty, shop share.'],
  total_material_cad_field: ['calc', 'Unit Price CAD × Qty, field share.'],
  total_material_usd_shop: ['calc', 'Total Material CAD - Shop × frozen FX rate.'],
  total_material_usd_field: ['calc', 'Total Material CAD - Field × frozen FX rate.'],
  labour_rate_cad_shop: ['config', 'Per-estimate shop labour rate (CAD). Set in ⚙️ Factors.'],
  labour_rate_cad_field: ['config', 'Per-estimate field labour rate (CAD). Set in ⚙️ Factors.'],
  labour_rate_usd_shop: ['calc', 'Shop labour rate × frozen FX rate.'],
  labour_rate_usd_field: ['calc', 'Field labour rate × frozen FX rate.'],
  total_labour_cad_shop: ['calc', 'Shop Hours × Shop Labour Rate (CAD).'],
  total_labour_cad_field: ['calc', 'Field Hours × Field Labour Rate (CAD).'],
  total_labour_usd_shop: ['calc', 'Total Labour CAD - Shop × frozen FX rate.'],
  total_labour_usd_field: ['calc', 'Total Labour CAD - Field × frozen FX rate.'],
  grand_total_cad: ['calc', 'Total Labour CAD + Total Material CAD.'],
  grand_total_cad_shop: ['calc', 'Shop labour + shop material (CAD).'],
  grand_total_cad_field: ['calc', 'Field labour + field material (CAD).'],
  grand_total_usd: ['calc', 'Grand Total CAD × frozen FX rate.'],
  grand_total_usd_shop: ['calc', 'Grand Total CAD - Shop × frozen FX rate.'],
  grand_total_usd_field: ['calc', 'Grand Total CAD - Field × frozen FX rate.'],
  grand_total_cad_after_factors: ['calc', 'Grand Total CAD with labour factors applied (labour only).'],
  grand_total_cad_after_factors_shop: ['calc', 'Shop grand total after shop+both factors.'],
  grand_total_cad_after_factors_field: ['calc', 'Field grand total after field+both factors.'],
  grand_total_usd_after_factors: ['calc', 'After-factors CAD × frozen FX rate.'],
  grand_total_usd_after_factors_shop: ['calc', 'Shop after-factors CAD × frozen FX rate.'],
  grand_total_usd_after_factors_field: ['calc', 'Field after-factors CAD × frozen FX rate.'],
  price_cut_to_pipe_factor: ['manual', 'Unit-price pricing factor (tracked separately from labour factors).'],
  fitting_to_fittings_factor: ['manual', 'Unit-price pricing factor.'],
  pipe_to_pipe_factor: ['manual', 'Unit-price pricing factor.'],
  pipe_to_pipe_qty: ['manual', 'Joints where two pipes meet, charged on unit-price work.'],
  class: ['import', 'Classification code.'],
  restrictions: ['import', 'Material/handling restrictions.'],
  support_code: ['import', 'Support reference code.'],
  trim_allowance: ['import', 'Trim allowance.'],
  rev: ['system', 'System revision label for this row.'],
  comments: ['manual', 'Free text.'],
};

// Client-side mirror of the backend factor row-scope matching (mtoFactors.js):
// case-insensitive "contains", comma-separated = any-of, all defined scopes AND.
const factorFilterMatch = (filterStr, value) => {
  const f = String(filterStr ?? '').trim();
  if (!f) return true;
  const v = String(value ?? '').trim().toLowerCase();
  if (!v) return false;
  return f.split(',').map(t => t.trim().toLowerCase()).filter(Boolean).some(t => v.includes(t));
};
const factorAppliesToRow = (factor, row) => {
  const scopeBore = String(factor.scope_bore || 'all').toLowerCase();
  if (scopeBore !== 'all') {
    const bore = String(row.bore || '').trim().toLowerCase();
    if (!bore) return false;
    if (scopeBore === 'sb' && bore !== 'sb') return false;
    if (scopeBore === 'lb' && bore !== 'lb' && bore !== 'xlb') return false;
  }
  if (!factorFilterMatch(factor.scope_type, row.type)) return false;
  if (!factorFilterMatch(factor.scope_material, row.material)) return false;
  if (!factorFilterMatch(factor.scope_sch, row.sch)) return false;
  const col = String(factor.scope_column || '').trim();
  const val = String(factor.scope_value ?? '').trim();
  if (col && val && !factorFilterMatch(val, row[col])) return false;
  return true;
};

const formatMtoValue = (val, column) => {
  if (val === null || val === undefined || val === '') return '-';
  if (column.type === 'number') {
    return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  // Text columns: return the raw value. (Returning null here previously made
  // EditableCell render blank, hiding every text column in the grid.)
  return val;
};

const formatCurrency = (num) => {
  if (num >= 1000000) return '$' + (num / 1000000).toFixed(2) + 'M';
  if (num >= 1000) return '$' + (num / 1000).toFixed(1) + 'K';
  return '$' + num.toFixed(2);
};

const measureTextWidth = (text, font) => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.font = font;
  return ctx.measureText(text).width;
};

const BTN = {
  padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: '500', whiteSpace: 'nowrap',
  transition: 'all 0.15s ease',
};

// Specs/Datasheet grid cell: shows the matched part's current spec (clickable to
// view inline) + a 📎 control to upload a new PDF version. Specs are versioned in
// the Part Rate DB per part_number, so a row must have a matched part to attach one.
const MtoDatasheetCell = memo(function MtoDatasheetCell({ item, instance, accounts, onUploaded, showToast }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const hasPart = !!item.part_number;

  const viewSpec = async () => {
    if (!item.datasheet_id) return;
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/datasheet/${item.datasheet_id}/file`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { showToast('Failed to open spec', 'error'); }
  };

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.type !== 'application/pdf') { showToast('Only PDF files are allowed', 'error'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const resp = await authenticatedApiCall(instance, accounts, 'POST', `/mto/${item.id}/spec`, fd);
      if (resp.data?.success) { showToast('Spec uploaded', 'success'); onUploaded && onUploaded(); }
      else showToast(resp.data?.message || 'Upload failed', 'error');
    } catch (err) { showToast(err?.response?.data?.message || 'Upload failed', 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
      {item.datasheet_id ? (
        <button onClick={viewSpec} title={item.datasheet_name || 'View spec'}
          style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', textDecoration: 'underline', cursor: 'pointer', fontSize: '0.75rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 130, flex: 1, textAlign: 'left' }}>
          📄 {item.datasheet_name || 'View'}
        </button>
      ) : (
        <span style={{ color: '#cbd5e1', fontSize: '0.75rem', flex: 1 }}>{hasPart ? 'No spec' : '—'}</span>
      )}
      <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={onPick} />
      <button onClick={() => hasPart && fileRef.current?.click()} disabled={!hasPart || busy}
        title={hasPart ? 'Upload a new spec/datasheet PDF (versioned per part)' : 'Match this row to a part first'}
        style={{ background: 'none', border: 'none', cursor: hasPart && !busy ? 'pointer' : 'not-allowed', opacity: hasPart && !busy ? 1 : 0.4, fontSize: '0.85rem' }}>
        {busy ? '⏳' : '📎'}
      </button>
    </div>
  );
});

const DEL_COL_WIDTH = 70;
const MTO_ROW_HEIGHT = 34;
const MTO_HEADER_HEIGHT = 40;

/**
 * Virtualized MTO row (react-window v2). Only ~viewport rows mount, so checkbox
 * clicks no longer reconcile thousands of EditableCells.
 */
const MtoVirtualRow = memo(function MtoVirtualRow({
  index, style, rows, visibleCols, selectedIds, onSelectRow, onDeleteRow,
  onSaveCell, savingRows, colWidths, instance, accounts, showToast, onUploaded,
}) {
  const item = rows[index];
  if (!item) return null;

  const isSelected = selectedIds.has(item.id);
  const isSaving = savingRows.has(item.id);
  const rowBg = isSaving ? '#fff3cd' : (isSelected ? '#eef2ff' : (index % 2 === 0 ? 'white' : '#f8fafc'));

  return (
    <div
      style={{
        ...style,
        display: 'flex',
        alignItems: 'stretch',
        backgroundColor: rowBg,
        borderBottom: '1px solid #f1f5f9',
        fontSize: '0.82rem',
      }}
    >
      <div style={{
        width: DEL_COL_WIDTH, minWidth: DEL_COL_WIDTH, maxWidth: DEL_COL_WIDTH,
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
        borderRight: '1px solid #f1f5f9', backgroundColor: rowBg, boxSizing: 'border-box',
      }}>
        <input
          type="checkbox"
          style={{ accentColor: '#6366f1', cursor: 'pointer' }}
          checked={isSelected}
          onChange={() => onSelectRow(item.id)}
          title="Select row"
        />
        <button
          onClick={() => onDeleteRow(item.id)}
          style={{ padding: '2px 6px', backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '3px', cursor: 'pointer', fontSize: '0.65rem' }}
          title="Delete"
        >
          🗑️
        </button>
      </div>
      {visibleCols.map((col) => {
        const w = colWidths[col.key] || col.width || 100;
        return (
          <div
            key={col.key}
            style={{
              width: w, minWidth: w, maxWidth: w, padding: '3px 6px',
              display: 'flex', alignItems: 'center',
              justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start',
              borderRight: '1px solid #f1f5f9', boxSizing: 'border-box', overflow: 'hidden',
              backgroundColor: isSaving ? '#fff3cd' : 'transparent',
            }}
          >
            {col.key === 'datasheet' ? (
              <MtoDatasheetCell item={item} instance={instance} accounts={accounts} showToast={showToast} onUploaded={onUploaded} />
            ) : col.type === 'factor' ? (
              factorAppliesToRow(col.factor, item)
                ? <span style={{ fontSize: '0.78rem', color: '#4338ca', fontWeight: 600 }} title="This factor multiplies this row's labour">×{Number(col.factor.multiplier)}</span>
                : <span style={{ fontSize: '0.78rem', color: '#cbd5e1' }} title="This factor does not apply to this row">—</span>
            ) : (
              <EditableCell
                value={item[col.key]}
                row={item}
                column={col}
                onSave={onSaveCell}
                disabled={!!col.readOnly}
                formatValue={formatMtoValue}
                accentColor="#6366f1"
              />
            )}
          </div>
        );
      })}
    </div>
  );
}, (prev, next) => {
  if (prev.index !== next.index || prev.style !== next.style) return false;
  if (prev.rows !== next.rows || prev.visibleCols !== next.visibleCols || prev.colWidths !== next.colWidths) return false;
  if (prev.savingRows !== next.savingRows) return false;
  const prevItem = prev.rows[prev.index];
  const nextItem = next.rows[next.index];
  if (prevItem !== nextItem) return false;
  if (!prevItem) return true;
  // Only re-render when THIS row's selection flips (not every Set change).
  return prev.selectedIds.has(prevItem.id) === next.selectedIds.has(nextItem.id);
});

const MtoLog = ({ projectId, jobNumber, estimateNumber, onFilterStateChange }) => {
  const { instance, accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const { toast, showToast } = useToast();
  const [savingRows, setSavingRows] = useState(new Set());
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newRow, setNewRow] = useState({});
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const [showAnalytics, setShowAnalytics] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [helpTab, setHelpTab] = useState('guide');
  const [showAuditLog, setShowAuditLog] = useState(false);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [showFactors, setShowFactors] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [showPurchasing, setShowPurchasing] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const saved = localStorage.getItem('mto_visible_columns');
      if (saved) {
        // Keep saved dynamic factor columns (factor_<id>) as well as static ones.
        const arr = JSON.parse(saved).filter(k => k.startsWith('factor_') || COLUMNS.some(c => c.key === k));
        if (arr.length) return new Set(arr);
      }
    } catch (e) { /* ignore */ }
    const defaults = DEFAULT_VISIBLE_COLUMNS.filter(k => COLUMNS.some(c => c.key === k));
    return new Set(defaults.length ? defaults : COLUMNS.map(c => c.key));
  });
  const [colWidths, setColWidths] = useState(() => {
    const w = {};
    COLUMNS.forEach(c => { w[c.key] = c.width; });
    try {
      const saved = localStorage.getItem('mto_col_widths');
      if (saved) Object.assign(w, JSON.parse(saved));
    } catch (e) { /* ignore */ }
    return w;
  });

  const fetchItemsRef = useRef(null);
  const itemsRef = useRef([]);
  const handleDataChange = useCallback(() => {
    if (fetchItemsRef.current) fetchItemsRef.current();
  }, []);

  const { viewers, isConnected, connectionState, socketId } = usePresence('mtoLog', projectId || jobNumber || 'global', true, handleDataChange);

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);
  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const filterHook = useTableFilter({ columns: COLUMNS, items });
  const sortHook = useTableSort({ columns: COLUMNS });

  const [showExportModal, setShowExportModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [mtoExporting, setMtoExporting] = useState(false);
  const [mtoUploadJobId, setMtoUploadJobId] = useState(null);
  const columnConfigRef = useRef(null);
  const [colSearch, setColSearch] = useState('');

  // Per-estimate labour factors — each one becomes a read-only grid column
  // (⚙️ prefix) showing the multiplier on the rows it applies to.
  const [estFactors, setEstFactors] = useState([]);
  const fetchEstFactors = useCallback(async () => {
    if (!estimateNumber) { setEstFactors([]); return; }
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/factors?estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (resp.data?.success) setEstFactors(resp.data.factors || []);
    } catch (err) { console.warn('Failed to load MTO factors:', err); }
  }, [instance, accounts, estimateNumber]);
  useEffect(() => { fetchEstFactors(); }, [fetchEstFactors]);

  // Static columns + one dynamic column per factor.
  const allColumns = useMemo(() => [
    ...COLUMNS,
    ...estFactors.map(f => ({ key: `factor_${f.id}`, label: `⚙️ ${f.name}`, type: 'factor', width: 130, readOnly: true, factor: f })),
  ], [estFactors]);

  const { columnOrder, moveColumn } = useColumnOrder('mto-column-order', allColumns);
  const visibleCols = useMemo(
    () => applyColumnOrder(allColumns, columnOrder).filter(c => visibleColumns.has(c.key)),
    [allColumns, columnOrder, visibleColumns]
  );

  // Persist the user's chosen columns + widths so they survive a page refresh.
  useEffect(() => {
    try { localStorage.setItem('mto_visible_columns', JSON.stringify([...visibleColumns])); } catch (e) { /* ignore */ }
  }, [visibleColumns]);
  useEffect(() => {
    try { localStorage.setItem('mto_col_widths', JSON.stringify(colWidths)); } catch (e) { /* ignore */ }
  }, [colWidths]);

  // ----- Plan 4: MTO revisions (Maestro-style, upload-driven) -----
  const [revisions, setRevisions] = useState([]);
  const [selectedRev, setSelectedRev] = useState('all'); // 'all' or a rev number
  const [canManageRev, setCanManageRev] = useState(false);

  const fetchItems = useCallback(async () => {
    try {
      // Soft refresh: only show the full-page spinner on the initial load so
      // presence-driven refetches don't unmount the virtualized grid.
      setError(null);
      setLoading((prev) => (itemsRef.current.length === 0 ? true : prev));
      const qp = [];
      if (jobNumber) qp.push(`job_number=${encodeURIComponent(jobNumber)}`);
      if (estimateNumber) qp.push(`estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (selectedRev !== 'all') qp.push(`rev=${encodeURIComponent(selectedRev)}`);
      const endpoint = qp.length ? `/mto?${qp.join('&')}` : '/mto';
      const response = await authenticatedApiCall(instance, accounts, 'GET', endpoint);
      if (response.data.success) setItems(response.data.data || []);
      else setError('Failed to load MTO data');
    } catch (err) { console.error('Error:', err); setError('Failed to load data. Please try again.'); }
    finally { setLoading(false); }
  }, [instance, accounts, jobNumber, estimateNumber, selectedRev]);

  const fetchRevisions = useCallback(async () => {
    if (!estimateNumber) { setRevisions([]); setCanManageRev(false); return; }
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/revisions?estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (resp.data?.success) { setRevisions(resp.data.revisions || []); setCanManageRev(!!resp.data.canManage); }
    } catch (err) { console.warn('Failed to load MTO revisions:', err); }
  }, [instance, accounts, estimateNumber]);

  useEffect(() => { fetchRevisions(); }, [fetchRevisions]);

  const selectedRevObj = useMemo(
    () => (selectedRev === 'all' ? null : revisions.find(r => String(r.rev) === String(selectedRev))),
    [selectedRev, revisions]
  );

  const handleBidClose = async () => {
    if (!selectedRevObj) { showToast('Select a specific revision to close.', 'error'); return; }
    if (!window.confirm(`Close the bid for revision ${selectedRevObj.rev}? This is a soft lock — edits are still possible but will be discouraged and audit-logged.`)) return;
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/revisions/close', { estimate_number: estimateNumber, rev: selectedRevObj.rev });
      if (resp.data?.success) { showToast(resp.data.message || 'Bid closed', 'success'); fetchRevisions(); }
      else showToast(resp.data?.message || 'Failed to close', 'error');
    } catch (err) { showToast(err?.response?.data?.message || 'Failed to close', 'error'); }
  };

  const handleBidReopen = async () => {
    if (!selectedRevObj) return;
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/revisions/reopen', { estimate_number: estimateNumber, rev: selectedRevObj.rev });
      if (resp.data?.success) { showToast(resp.data.message || 'Revision reopened', 'success'); fetchRevisions(); }
      else showToast(resp.data?.message || 'Failed to reopen', 'error');
    } catch (err) { showToast(err?.response?.data?.message || 'Failed to reopen', 'error'); }
  };

  // Soft-delete an entire revision (TC / PM). Data stays in the database.
  const handleDeleteRevision = async () => {
    if (!selectedRevObj) { showToast('Select a specific revision to delete.', 'error'); return; }
    if (!window.confirm(`Delete revision ${selectedRevObj.rev} and all ${selectedRevObj.row_count} of its rows?\n\nThis is a soft delete — the data stays in the database and can be restored, but the revision will no longer appear in the portal.`)) return;
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/revisions/delete', { estimate_number: estimateNumber, rev: selectedRevObj.rev });
      if (resp.data?.success) {
        showToast(resp.data.message || 'Revision deleted', 'success');
        setSelectedRev('all');
        fetchRevisions();
        fetchItems();
      } else showToast(resp.data?.message || 'Failed to delete revision', 'error');
    } catch (err) { showToast(err?.response?.data?.message || 'Failed to delete revision', 'error'); }
  };

  // Plan 5: push the MTO (summarized by ISO sheet) into tracker_piping.
  const handlePushToTracker = async () => {
    if (!estimateNumber) { showToast('Open a single estimate to push its MTO to the tracker.', 'error'); return; }
    const revLabel = selectedRev === 'all' ? 'all rows' : (selectedRev === 'iso' ? 'consolidated ISOs (latest per ISO)' : `revision ${selectedRev}`);
    const jobLabel = jobNumber ? `job ${jobNumber}` : 'its awarded job';
    if (!window.confirm(`Push the MTO (${revLabel}) for estimate ${estimateNumber} into the piping tracker for ${jobLabel}?\n\nBudget hours per ISO sheet will be created/refreshed. Existing field progress is preserved.`)) return;
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'POST', '/mto/push-to-tracker', {
        estimate_number: estimateNumber, job_number: jobNumber || undefined, rev: selectedRev,
      });
      if (resp.data?.success) showToast(resp.data.message || 'Pushed to tracker', 'success');
      else showToast(resp.data?.message || 'Failed to push', 'error');
    } catch (err) { showToast(err?.response?.data?.message || 'Failed to push to tracker', 'error'); }
  };

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { fetchItemsRef.current = fetchItems; }, [fetchItems]);
  useEffect(() => { itemsRef.current = items; }, [items]);

  // ----- Plan 1a: per-estimate frozen CAD->USD rate -----
  const [fxStatus, setFxStatus] = useState(null); // { data, suggestedRate, isFrozen }
  const [fxInput, setFxInput] = useState('');
  const [fxSaving, setFxSaving] = useState(false);

  const fetchFxStatus = useCallback(async () => {
    if (!estimateNumber) { setFxStatus(null); return; }
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/mto/fx-rate?estimate_number=${encodeURIComponent(estimateNumber)}`);
      if (resp.data?.success) {
        setFxStatus(resp.data);
        const current = resp.data.data?.cad_to_usd_rate ?? resp.data.suggestedRate ?? '';
        setFxInput(current === null || current === undefined ? '' : String(current));
      }
    } catch (err) { console.warn('Failed to load fx-rate status:', err); }
  }, [instance, accounts, estimateNumber]);

  useEffect(() => { fetchFxStatus(); }, [fetchFxStatus]);

  const handleFreezeFxRate = async () => {
    const rate = parseFloat(fxInput);
    if (!rate || !Number.isFinite(rate) || rate <= 0) { showToast('Enter a valid positive rate to freeze.', 'error'); return; }
    if (!window.confirm(`Freeze CAD→USD rate at ${rate} for estimate ${estimateNumber}? USD columns will be recalculated and the rate will be locked (superadmin can change later).`)) return;
    try {
      setFxSaving(true);
      const resp = await authenticatedApiCall(instance, accounts, 'PUT', '/mto/fx-rate', {
        estimate_number: estimateNumber, rate, freeze: true, suggested_rate: fxStatus?.suggestedRate ?? null, job_number: jobNumber,
      });
      if (resp.data?.success) { showToast('Conversion rate frozen. USD columns updated.', 'success'); await fetchFxStatus(); fetchItems(); }
      else showToast(resp.data?.message || 'Failed to freeze rate', 'error');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to freeze rate', 'error');
    } finally { setFxSaving(false); }
  };

  // Clear the column-search box whenever the dropdown closes.
  useEffect(() => { if (!showColumnConfig) setColSearch(''); }, [showColumnConfig]);

  // Close column config on outside click
  useEffect(() => {
    if (!showColumnConfig) return;
    const handler = (e) => {
      if (columnConfigRef.current && !columnConfigRef.current.contains(e.target)) setShowColumnConfig(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColumnConfig]);

  const autoFitColumns = useCallback(() => {
    const CELL_PAD = 24, MIN_W = 60, MAX_W = 400;
    const HEADER_FONT = 'bold 13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const CELL_FONT = '13px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
    const HEADER_PAD = 50;
    const cols = visibleCols;
    const sampled = items.length > 200 ? items.slice(0, 200) : items;
    const newWidths = {};
    cols.forEach(col => {
      const headerW = measureTextWidth(col.label, HEADER_FONT) + HEADER_PAD;
      let maxCellW = 0;
      for (const row of sampled) {
        const raw = row[col.key];
        let val;
        if (col.type === 'number' && raw != null) val = Number(raw).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        else if (col.type === 'date' && raw) val = new Date(raw).toLocaleDateString();
        else val = String(raw || '-');
        const w = measureTextWidth(val, CELL_FONT) + CELL_PAD;
        if (w > maxCellW) maxCellW = w;
      }
      newWidths[col.key] = Math.max(MIN_W, Math.min(MAX_W, Math.ceil(Math.max(headerW, maxCellW))));
    });
    setColWidths(prev => ({ ...prev, ...newWidths }));
    showToast('Columns auto-fitted', 'success');
  }, [items, visibleCols, showToast]);

  const toggleColumn = (key) => {
    setVisibleColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); }
      else next.add(key);
      return next;
    });
  };

  const showAllColumns = () => setVisibleColumns(new Set(allColumns.map(c => c.key)));
  const hideAllColumns = () => {
    const essential = new Set(['job_number', 'description', 'type', 'qty', 'grand_total_cad']);
    setVisibleColumns(essential);
  };

  const summaryStats = useMemo(() => {
    const totalQty = items.reduce((s, d) => s + (parseFloat(d.qty) || 0), 0);
    const totalCAD = items.reduce((s, d) => s + (parseFloat(d.grand_total_cad) || 0), 0);
    const totalHR = items.reduce((s, d) => s + (parseFloat(d.total_hours) || 0), 0);
    const totalJoints = items.reduce((s, d) => s + (parseFloat(d.joints) || 0), 0);
    return { totalQty, totalCAD, totalHR, totalJoints };
  }, [items]);

  const handleSaveCell = useCallback(async (rowId, columnKey, newValue) => {
    // Unit Price CAD is derived from the matched part's cost in the Part Rate DB —
    // it can't be edited on the MTO line. Don't hit the API (the backend would
    // silently overwrite it and report "saved"); instead guide the user to the
    // correct workflow. The cell reverts to its computed value on its own.
    if (columnKey === 'unit_price_cad') {
      showToast('Unit Price CAD is set from the Part Rate DB — the right process is to add/update the cost there (💲 Import Cost).', 'error');
      return;
    }
    setSavingRows(prev => new Set(prev).add(rowId));
    try {
      const col = COLUMNS.find(c => c.key === columnKey);
      const processedValue = col?.type === 'number' ? (newValue === '' ? null : Number(newValue)) : newValue;
      const response = await authenticatedApiCall(instance, accounts, 'PUT', `/mto/${rowId}`, { [columnKey]: processedValue }, syncHeaders());
      if (response.data.success) {
        // The backend re-matches the Part Rate DB and recomputes all derived
        // columns (mh_per_ea, total_hours, labour/cost totals). Apply the full
        // recalculated row it returns so calculated cells update live instead of
        // going stale until a manual refresh.
        const updatedRow = response.data.data;
        setItems(prev => prev.map(item => item.id === rowId
          ? (updatedRow ? { ...item, ...updatedRow } : { ...item, [columnKey]: processedValue })
          : item));
        showToast('Saved successfully');
      }
      else throw new Error(response.data.message || 'Save failed');
    } catch (err) { showToast(`Save failed: ${err.message}`, 'error'); throw err; }
    finally { setSavingRows(prev => { const s = new Set(prev); s.delete(rowId); return s; }); }
  }, [instance, accounts, syncHeaders, showToast]);

  const handleAddNewRow = async () => {
    try {
      const rowData = { ...newRow }; if (jobNumber) rowData.job_number = jobNumber; if (estimateNumber) rowData.estimate_number = estimateNumber;
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/mto', rowData, syncHeaders());
      if (response.data.success) { setItems(prev => [response.data.data, ...prev]); setNewRow({}); setIsAddingNew(false); showToast('New record added'); }
      else throw new Error(response.data.message);
    } catch (err) { showToast(`Failed: ${err.message}`, 'error'); }
  };

  const handleDeleteRow = useCallback(async (rowId) => {
    if (!window.confirm('Delete this record? (Soft delete — the data is retained in the database.)')) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'DELETE', `/mto/${rowId}`, null, syncHeaders());
      if (response.data.success) {
        setItems(prev => prev.filter(item => item.id !== rowId));
        setSelectedIds(prev => { const s = new Set(prev); s.delete(rowId); return s; });
        showToast('Deleted');
      } else throw new Error(response.data.message);
    } catch (err) { showToast(`Delete failed: ${err.message}`, 'error'); }
  }, [instance, accounts, syncHeaders, showToast]);

  const toggleSelect = useCallback((rowId) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(rowId)) s.delete(rowId); else s.add(rowId);
      return s;
    });
  }, []);

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} selected record(s)? (Soft delete — the data is retained in the database.)`)) return;
    try {
      const response = await authenticatedApiCall(instance, accounts, 'POST', '/mto/bulk-delete', { ids, job_number: jobNumber }, syncHeaders());
      if (response.data.success) {
        const idSet = new Set(ids);
        setItems(prev => prev.filter(item => !idSet.has(item.id)));
        setSelectedIds(new Set());
        showToast(`Deleted ${response.data.deletedCount} record(s)`);
      } else throw new Error(response.data.message);
    } catch (err) { showToast(`Delete failed: ${err.message}`, 'error'); }
  };

  const handleExport = async (mode) => {
    if (mtoExporting) return;
    try {
      setMtoExporting(true);
      setShowExportModal(false);
      const cols = mode === 'dashboard' ? visibleCols : COLUMNS;
      const rows = mode === 'template' ? [] : filteredItems;
      const suffix = mode === 'template' ? 'template' : mode;
      const namePart = jobNumber ? `MTO_${jobNumber}` : 'MTO';
      await exportToExcel({ columns: cols, rows, fileName: `${namePart}_${suffix}.xlsx`, sheetName: 'MTO' });
      showToast(mode === 'template' ? 'Template downloaded' : `Exported ${rows.length} rows`, 'success');
    } catch (error) {
      showToast(`Export failed: ${error.message}`, 'error');
    } finally {
      setMtoExporting(false);
    }
  };

  const handleUploadSuccess = (data) => {
    if (data?.jobId) { setMtoUploadJobId(data.jobId); showToast('Import started — matching parts to the Part Rate DB...', 'success'); }
    else { showToast(`MTO upload completed! ${data.rowsProcessed || 0} rows processed.`, 'success'); fetchItems(); }
  };
  const handleUploadError = (message) => { showToast(`MTO upload failed: ${message}`, 'error'); };

  const handleUploadJobComplete = (jobData) => {
    setMtoUploadJobId(null);
    let result = jobData?.result || jobData?.result_data || {};
    // Job results are persisted in job_data (JSON) — parse if that's all we got.
    if (!result.insertedRows && jobData?.job_data) {
      try { result = typeof jobData.job_data === 'string' ? JSON.parse(jobData.job_data) : jobData.job_data; } catch (e) { /* ignore */ }
    }
    const inserted = result.insertedRows ?? jobData?.processed_rows ?? 0;
    const rejected = result.rejectedRows ?? 0;
    const newRev = result.revision;
    if (rejected > 0) {
      showToast(`Import done: ${inserted} imported, ${rejected} not matched to Part Rate DB — an email was sent with the details.`, 'error');
    } else {
      showToast(`Import complete: ${inserted} row(s) imported${newRev !== null && newRev !== undefined ? ` as revision ${newRev}` : ''}.`, 'success');
    }
    // Plan D/E: surface auto-push to tracker + auto-hold / change order results.
    const ap = result.autoPush;
    if (ap && (ap.inserted || ap.updated)) {
      showToast(`Tracker updated automatically: ${ap.inserted} new + ${ap.updated} refreshed ISO sheet(s).`, 'success');
    }
    const cm = result.changeMgmt;
    if (cm && (cm.held || cm.ccrId || cm.earnedCcrId)) {
      const parts = [];
      if (cm.held) parts.push(`${cm.held} ISO(s) put ON HOLD`);
      if (cm.ccrId) parts.push(`scope CO CCR #${cm.ccrId}`);
      if (cm.earnedCcrId) {
        const hrs = cm.earnedHrs != null ? ` (${Number(cm.earnedHrs).toFixed(1)} earned hrs)` : '';
        parts.push(`earned-hours CO CCR #${cm.earnedCcrId}${hrs}`);
      }
      showToast(`Change management: ${parts.join(', ')}. Check Change Orders Tracking.`, 'error');
    }
    // Plan 4: refresh revisions and jump to the newly-created one.
    fetchRevisions();
    if (newRev !== null && newRev !== undefined) setSelectedRev(String(newRev));
    else fetchItems();
  };
  const handleUploadJobError = (message) => { setMtoUploadJobId(null); showToast(`MTO import failed: ${message}`, 'error'); };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (filterHook.openFilterColumn) { filterHook.setOpenFilterColumn(null); filterHook.setFilterSearch(''); }
        if (showColumnConfig) setShowColumnConfig(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [filterHook.openFilterColumn, filterHook, showColumnConfig]);

  const hasActiveFilters = filterHook.hasActiveFilters || searchTerm;

  const handleClearAll = useCallback(() => {
    filterHook.clearAllFilters(); setSearchTerm(''); sortHook.clearSort();
    showToast('All filters cleared', 'success');
  }, [filterHook, sortHook, showToast]);

  useEffect(() => {
    if (onFilterStateChange) {
      onFilterStateChange({
        hasActiveFilters, searchQuery: searchTerm,
        activeFilters: filterHook.activeFilters.map(f => ({ columnName: f.label, type: f.type, count: f.count, mode: f.mode, value: f.value })),
        onClearAll: handleClearAll
      });
    }
    return () => { if (onFilterStateChange) onFilterStateChange(null); };
  }, [hasActiveFilters, searchTerm, filterHook.activeFilters, handleClearAll, onFilterStateChange]);

  const filteredItems = useMemo(() => {
    let result = [...items];
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      result = result.filter(item => COLUMNS.some(col => { const val = item[col.key]; return val !== null && val !== undefined && String(val).toLowerCase().includes(search); }));
    }
    result = filterHook.applyFilters(result);
    result = sortHook.applySorting(result);
    return result;
  }, [items, searchTerm, filterHook.selectedValues, filterHook.textFilters, filterHook.numericFilters, filterHook.dateFilters, filterHook.applyFilters, sortHook.sortColumn, sortHook.sortDirection, sortHook.applySorting]);

  const getColWidth = (key) => colWidths[key] || allColumns.find(c => c.key === key)?.width || 100;

  const filteredItemIds = useMemo(() => filteredItems.map((r) => r.id), [filteredItems]);

  const selectionSummary = useMemo(() => {
    if (filteredItems.length === 0) return { allSelected: false, someSelected: false };
    let selectedCount = 0;
    for (const item of filteredItems) {
      if (selectedIds.has(item.id)) selectedCount += 1;
    }
    return {
      allSelected: selectedCount === filteredItems.length,
      someSelected: selectedCount > 0 && selectedCount < filteredItems.length,
    };
  }, [filteredItems, selectedIds]);

  const footerMeta = useMemo(() => {
    const types = new Set();
    const materials = new Set();
    for (const d of items) {
      if (d.type) types.add(d.type);
      if (d.material) materials.add(d.material);
    }
    return { typeCount: types.size, materialCount: materials.size };
  }, [items]);

  const rowProps = useMemo(() => ({
    rows: filteredItems,
    visibleCols,
    selectedIds,
    onSelectRow: toggleSelect,
    onDeleteRow: handleDeleteRow,
    onSaveCell: handleSaveCell,
    savingRows,
    colWidths,
    instance,
    accounts,
    showToast,
    onUploaded: fetchItems,
  }), [
    filteredItems, visibleCols, selectedIds, toggleSelect, handleDeleteRow,
    handleSaveCell, savingRows, colWidths, instance, accounts, showToast, fetchItems,
  ]);

  const handleSelectAllFiltered = useCallback((e) => {
    if (e.target.checked) setSelectedIds(new Set(filteredItems.map((i) => i.id)));
    else setSelectedIds(new Set());
  }, [filteredItems]);

  const closePurchasing = useCallback(() => setShowPurchasing(false), []);

  const handleResizeStart = (colKey, startWidth) => (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const onMouseMove = (moveE) => {
      const diff = moveE.clientX - startX;
      setColWidths(prev => ({ ...prev, [colKey]: Math.max(40, startWidth + diff) }));
    };
    const onMouseUp = () => { document.removeEventListener('mousemove', onMouseMove); document.removeEventListener('mouseup', onMouseUp); };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  if (loading) return (
    <div style={{ textAlign: 'center', padding: '3rem' }}>
      <div style={{ display: 'inline-block', width: '48px', height: '48px', border: '4px solid #f3f3f3', borderTop: '4px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
      <p style={{ marginTop: '1rem', color: '#666' }}>Loading MTO data...</p>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );

  if (error) return (
    <div style={{ padding: '2rem', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '8px', color: '#856404' }}>
      <strong>Warning:</strong> {error}
      <button onClick={fetchItems} style={{ marginLeft: '1rem', padding: '0.5rem 1rem', backgroundColor: '#6366f1', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Retry</button>
    </div>
  );

  const totalTableWidth = visibleCols.reduce((sum, col) => sum + getColWidth(col.key), 0) + DEL_COL_WIDTH;
  const tableHeight = Math.max(320, (typeof window !== 'undefined' ? window.innerHeight : 900) - 380);

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc' }}>
      {toast.show && <div style={{ position: 'fixed', top: '20px', right: '20px', padding: '1rem 1.5rem', backgroundColor: toast.type === 'success' ? '#10b981' : '#ef4444', color: 'white', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999, fontSize: '0.9rem', maxWidth: '420px' }}>{toast.message}</div>}
      {mtoUploadJobId && (
        <div style={{ position: 'fixed', top: '70px', right: '20px', width: '320px', zIndex: 9998 }}>
          <JobStatusMonitor jobId={mtoUploadJobId} onComplete={handleUploadJobComplete} onError={handleUploadJobError} />
        </div>
      )}

      {/* ── Header ── */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e2e8f0',
        padding: '1rem 1.5rem',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', flexShrink: 0,
      }}>
        <div>
          <h2 style={{ margin: 0, color: '#1e293b', fontSize: '1.35rem', fontWeight: '700' }}>📐 MTO Dashboard</h2>
          <p style={{ margin: '0.15rem 0 0', color: '#64748b', fontSize: '0.8rem' }}>
            {items.length} records{estimateNumber ? ` • Estimate ${estimateNumber}` : ''}{jobNumber ? ` • Job ${jobNumber}` : ''} — Qty: {summaryStats.totalQty.toLocaleString()} | Cost: {formatCurrency(summaryStats.totalCAD)} | Hrs: {summaryStats.totalHR.toLocaleString()} | Joints: {summaryStats.totalJoints.toLocaleString()}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={true} />
        </div>
      </div>

      {/* ── CAD→USD conversion rate (Plan 1a) ── */}
      {estimateNumber && fxStatus && (
        fxStatus.isFrozen ? (
          <div style={{ padding: '0.4rem 1rem', backgroundColor: '#ecfdf5', borderBottom: '1px solid #a7f3d0', color: '#065f46', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            🔒 CAD→USD rate frozen at <strong>{Number(fxStatus.data?.cad_to_usd_rate).toFixed(6)}</strong>
            {fxStatus.data?.frozen_by ? ` by ${fxStatus.data.frozen_by}` : ''}{fxStatus.data?.frozen_at ? ` on ${new Date(fxStatus.data.frozen_at).toLocaleDateString()}` : ''}. USD columns use this rate.
          </div>
        ) : (
          <div style={{ padding: '0.5rem 1rem', backgroundColor: '#fffbeb', borderBottom: '1px solid #fde68a', color: '#92400e', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', flexShrink: 0 }}>
            <span>⚠️ <strong>CAD→USD rate not frozen</strong> for estimate {estimateNumber}. USD columns stay blank until the PM freezes a rate.</span>
            <input type="number" step="0.0001" value={fxInput} onChange={(e) => setFxInput(e.target.value)} placeholder="rate"
              style={{ width: '110px', padding: '3px 8px', border: '1px solid #fcd34d', borderRadius: '4px', fontSize: '0.78rem' }} />
            {fxStatus.suggestedRate ? <span style={{ fontSize: '0.72rem', color: '#a16207' }}>(BoC today ≈ {Number(fxStatus.suggestedRate).toFixed(4)})</span> : null}
            <button onClick={handleFreezeFxRate} disabled={fxSaving}
              style={{ padding: '4px 10px', backgroundColor: '#d97706', color: 'white', border: 'none', borderRadius: '4px', cursor: fxSaving ? 'not-allowed' : 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>
              {fxSaving ? 'Freezing…' : 'Freeze rate'}
            </button>
          </div>
        )
      )}

      {/* ── MTO revisions (Plan 4) ── */}
      {estimateNumber && (
        <div style={{ padding: '0.4rem 1rem', backgroundColor: '#f1f5f9', borderBottom: '1px solid #e2e8f0', color: '#334155', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', flexShrink: 0 }}>
          <span style={{ fontWeight: 600 }}>📑 Revision:</span>
          <select value={selectedRev} onChange={(e) => setSelectedRev(e.target.value)}
            style={{ padding: '3px 8px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '0.78rem' }}>
            <option value="all">All rows (every stream)</option>
            {revisions.some((r) => r.rev_type === 'iso_batch') && (
              <option value="iso">🧩 Consolidated ISOs (latest per ISO)</option>
            )}
            {revisions.filter((r) => (r.rev_type || 'client') === 'client').length > 0 && (
              <optgroup label="Client revisions (Excel from client)">
                {revisions.filter((r) => (r.rev_type || 'client') === 'client').map((r) => (
                  <option key={r.rev} value={String(r.rev)}>
                    Rev {r.rev}{r.client_rev ? ` · client ${r.client_rev}` : ''}{r.transmittal_number ? ` · TX ${r.transmittal_number}` : ''}{r.status === 'closed' ? ' (closed)' : ''} · {r.row_count} rows
                  </option>
                ))}
              </optgroup>
            )}
            {revisions.some((r) => r.rev_type === 'iso_batch') && (
              <optgroup label="ISO batches (issued over the job)">
                {revisions.filter((r) => r.rev_type === 'iso_batch').map((r) => (
                  <option key={r.rev} value={String(r.rev)}>
                    ISO Batch {r.batch_no ?? '?'}{r.transmittal_number ? ` · TX ${r.transmittal_number}` : ''} · {r.row_count} rows{r.created_at ? ` · ${new Date(r.created_at).toLocaleDateString()}` : ''}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {revisions.length === 0 && <span style={{ color: '#94a3b8' }}>No revisions yet — the first upload creates Rev 0.</span>}
          {revisions.length >= 2 && (
            <button onClick={() => setShowCompare(true)} style={{ padding: '4px 10px', backgroundColor: '#fff', color: '#0369a1', border: '1px solid #bae6fd', borderRadius: '4px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>📈 Compare revisions</button>
          )}
          {selectedRevObj && canManageRev && (
            selectedRevObj.status === 'closed' ? (
              <button onClick={handleBidReopen} style={{ padding: '4px 10px', backgroundColor: '#fff', color: '#b45309', border: '1px solid #fcd34d', borderRadius: '4px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>Reopen bid</button>
            ) : (
              <button onClick={handleBidClose} style={{ padding: '4px 10px', backgroundColor: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>🔒 Close bid (Rev {selectedRevObj.rev})</button>
            )
          )}
          {selectedRevObj && canManageRev && (
            <button onClick={handleDeleteRevision} title="Soft-delete this revision (data is retained and can be restored)"
              style={{ padding: '4px 10px', backgroundColor: '#fff', color: '#b91c1c', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '0.76rem', fontWeight: 600 }}>🗑️ Delete Rev {selectedRevObj.rev}</button>
          )}
        </div>
      )}

      {/* Soft-lock banner for a closed revision (Plan 4) */}
      {selectedRevObj && selectedRevObj.status === 'closed' && (
        <div style={{ padding: '0.45rem 1rem', backgroundColor: '#fff7ed', borderBottom: '1px solid #fed7aa', color: '#9a3412', fontSize: '0.78rem', flexShrink: 0 }}>
          🔒 <strong>Bid closed</strong> for Rev {selectedRevObj.rev}
          {selectedRevObj.closed_by ? ` by ${selectedRevObj.closed_by}` : ''}{selectedRevObj.closed_at ? ` on ${new Date(selectedRevObj.closed_at).toLocaleDateString()}` : ''}.
          {' '}Edits are <strong>discouraged</strong> and audit-logged (soft lock).
        </div>
      )}

      {/* ── Toolbar ── */}
      <div style={{
        padding: '0.5rem 1rem', backgroundColor: 'white', borderBottom: '1px solid #e2e8f0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <div style={{ position: 'relative' }}>
            <input type="text" placeholder="Search all columns..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} style={{ padding: '6px 12px 6px 28px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.8rem', width: '220px', outline: 'none' }} />
            <span style={{ position: 'absolute', left: '8px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.75rem' }}>🔍</span>
          </div>
          <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
            {filteredItems.length === items.length ? `${items.length} records` : `${filteredItems.length} of ${items.length}`}
          </span>
          {hasActiveFilters && (
            <button onClick={handleClearAll} style={{ padding: '3px 8px', backgroundColor: '#fef2f2', color: '#ef4444', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: '500' }}>Clear Filters</button>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap', position: 'relative' }}>
          <button onClick={autoFitColumns} style={BTN}>↔️ Auto-fit</button>
          <div style={{ position: 'relative' }} ref={columnConfigRef}>
            <button onClick={() => setShowColumnConfig(!showColumnConfig)} style={{ ...BTN, backgroundColor: showColumnConfig ? '#6366f1' : '#f3f4f6', color: showColumnConfig ? 'white' : '#374151', borderColor: showColumnConfig ? '#6366f1' : '#e5e7eb' }}>⚙️ Columns</button>
            {showColumnConfig && (
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: '4px',
                backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                padding: '12px', zIndex: 1000, width: '340px', maxHeight: '440px', overflowY: 'auto',
              }}>
                <ColumnConfigPanel
                  title="Columns"
                  columns={applyColumnOrder(allColumns, columnOrder)}
                  isVisible={(key) => visibleColumns.has(key)}
                  onToggle={toggleColumn}
                  onMove={moveColumn}
                  onShowAll={showAllColumns}
                  onHideAll={hideAllColumns}
                />
              </div>
            )}
          </div>
          <button onClick={() => setShowAnalytics(true)} style={BTN}>📊 Analytics</button>
          <button onClick={() => setShowHelp(true)} style={BTN}>❓ Help</button>
          <button onClick={() => setShowAuditLog(true)} style={BTN}>📜 Audit Log</button>
          <button onClick={() => setShowFactors(true)} style={BTN} disabled={!estimateNumber} title={estimateNumber ? 'Factors, shop/field ratio & labour rates' : 'Open a single estimate to manage factors'}>⚙️ Factors</button>
          <button onClick={() => setShowExportModal(true)} style={BTN}>📤 Export</button>
          <button onClick={() => setShowUploadModal(true)} style={BTN}>📥 Import</button>
          {canManageRev && (estimateNumber || jobNumber) && (
            <button onClick={handlePushToTracker} style={{ ...BTN, backgroundColor: '#eef2ff', color: '#4338ca', borderColor: '#c7d2fe' }} title="Summarize the MTO by ISO sheet and push budget hours into the Piping Progress Tracker">➡️ Transfer to Progress Tracker</button>
          )}
          {(estimateNumber || jobNumber) && (
            <button onClick={() => setShowPurchasing(true)} style={{ ...BTN, backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0' }} title="Email a filtered MTO Excel to the purchasing team for supplier pricing">🛒 Send to Purchasing</button>
          )}
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} style={{ ...BTN, backgroundColor: '#fef2f2', color: '#dc2626', borderColor: '#fecaca' }}>🗑️ Delete ({selectedIds.size})</button>
          )}
          <button onClick={() => setIsAddingNew(true)} style={BTN}>➕ Add New</button>
        </div>
      </div>

      {/* ── Help modal (Plan 1) ── */}
      {showHelp && (
        <div onClick={() => setShowHelp(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '10px', maxWidth: (helpTab === 'columns' || helpTab === 'tour') ? '1000px' : '760px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ position: 'sticky', top: 0, background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)', color: 'white', padding: '1rem 1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.1rem' }}>📐 MTO — Help</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="mto-log-help" title="MTO Log Help" accent="#6366f1" dark />
                <button onClick={() => { setShowHelp(false); setHelpTab('guide'); }} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            </div>
            {/* Tab bar */}
            <div style={{ display: 'flex', borderBottom: '1px solid #e2e8f0', backgroundColor: '#f8fafc' }}>
              {[
                { id: 'guide', label: 'How it works' },
                { id: 'columns', label: 'All Columns' },
                ...(hasHelpTour('mto-log-help') ? [{ id: 'tour', label: 'Video Tour' }] : []),
              ].map(tab => (
                <button key={tab.id} onClick={() => setHelpTab(tab.id)} style={{ padding: '10px 20px', border: 'none', borderBottom: helpTab === tab.id ? '3px solid #6366f1' : '3px solid transparent', backgroundColor: helpTab === tab.id ? 'white' : 'transparent', color: helpTab === tab.id ? '#4f46e5' : '#6b7280', fontWeight: helpTab === tab.id ? 600 : 400, cursor: 'pointer', fontSize: '0.85rem' }}>{tab.label}</button>
              ))}
            </div>
            <div style={{ padding: '1.25rem 1.5rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.6 }}>
              {helpTab === 'tour' && (
                <HelpVideoTour guideId="mto-log-help" title="MTO Log Help" accent="#6366f1" compact showHeader />
              )}
              {helpTab === 'guide' && (<>
              <h4 style={{ margin: '0 0 .35rem' }}>Part Rate DB matching</h4>
              <p style={{ margin: '0 0 .9rem' }}>On import/save, each row is matched against the <strong>Part Rate DB</strong> to pull in the <strong>Part #</strong>, <strong>MH per EA</strong> (OTTAV hours), <strong>Joints</strong>, <strong>Joint Type</strong>, <strong>Dia In</strong> (per-each), and <strong>Unit Price CAD</strong> (cost). Matching uses these <strong>5 columns</strong>: <strong>type, material, size, SCH, rating</strong>. End Type and Seam Type are kept on the row for reference but are not part of the match. New rows that don't match are <strong>not imported</strong> — the uploader is emailed the list so the items can be added to the Part Rate DB first, then re-uploaded.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Calculated columns (read-only)</h4>
              <ul style={{ margin: '0 0 .9rem', paddingLeft: '1.2rem' }}>
                <li><strong>Bore</strong>: from Size — ≤2″ = SB, ≤24″ = LB, &gt;24″ = XLB.</li>
                <li><strong>Mat Modified for MatBore</strong>: material family (SS / CS / PVC / HDPE) by industry-standard ASTM rules; <strong>MatBore</strong> = family-bore (e.g. <code>SS-LB</code>). Mappings are adjustable in the material-family lookup; unrecognized materials are flagged <code>REVIEW</code>.</li>
                <li><strong>Total Joints</strong> = Joints × Qty.</li>
                <li><strong>Dia In</strong> comes <strong>directly from the Part Rate DB</strong> (per-each) on match. &nbsp; <strong>Total Dia In</strong> = Dia In × Qty.</li>
                <li><strong>Total Hours</strong> = Qty × MH per EA. Hours are based on <strong>OTTAV hours</strong>. <em>MCAA hours</em> are also stored in the Part Rate DB but are set aside for now — OTTAV is the single basis for all hour and labour calculations.</li>
                <li><strong>Total Material CAD</strong> = Unit Price CAD × Qty.</li>
                <li><strong>Total Labour CAD</strong> = (Shop Hours × Shop Labour Rate) + (Field Hours × Field Labour Rate).</li>
                <li><strong>Grand Total CAD</strong> = Total Labour CAD + Total Material CAD.</li>
              </ul>

              <h4 style={{ margin: '0 0 .35rem' }}>Shop / Field split</h4>
              <p style={{ margin: '0 0 .9rem' }}>Every hour and cost column is split into a <strong>- Shop</strong> and a <strong>- Field</strong> allocation using the estimate's <strong>shop/field ratio</strong> (e.g. 60 % shop ⇒ Shop = value × 0.60, Field = value × 0.40). Labour cost uses two per-estimate rates — a <strong>Shop Labour Rate</strong> applied to shop hours and a <strong>Field Labour Rate</strong> applied to field hours. Set the ratio and rates in <strong>⚙️ Factors</strong>.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>CAD → USD conversion</h4>
              <p style={{ margin: '0 0 .9rem' }}>Every <strong>USD</strong> column = its CAD value × the <strong>frozen per-estimate conversion rate</strong>. The PM freezes the rate at the start of the job (a Bank of Canada rate is suggested as a default). <strong>Until the rate is frozen, all USD columns stay blank</strong> and a reminder is shown/emailed. Once frozen, only a superadmin can change it.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Factors &amp; "after factors" totals</h4>
              <p style={{ margin: '0 0 .9rem' }}>Open <strong>⚙️ Factors</strong> to build a per-estimate list of <strong>labour factors</strong> (up to 20). Each factor has a <strong>multiplier</strong> and applies to <strong>Shop</strong>, <strong>Field</strong>, or <strong>Both</strong>. Pick from suggested factors (Oxygen Clean, Field Length, Shop/Field Small Bore, Support, Sloped Pipe) or add your own. A factor can also be <strong>limited to specific rows</strong>: small bore / large bore only, and contains-filters on <strong>type, material, schedule</strong> or <strong>any other column</strong> (e.g. only Piping Class "CS150"). Factors multiply <strong>labour only</strong> (never material): a row's shop labour is multiplied by the product of the shop+both factors that match it, the field labour by the matching field+both factors. The result appears in the six <strong>Grand Total (After Factors)</strong> columns — base, Shop, and Field for both CAD and USD. Every factor also appears as a read-only <strong>⚙️ column</strong> in the grid (enable it via ⚙️ Columns) showing the multiplier on the rows it applies to. The three <em>pricing</em> factors (Price Cut to Pipe, Fitting to Fittings, Pipe to Pipe) are for unit-price jobs and are tracked separately. <em>Pipe to Pipe Qty</em> charges the joints where two pipes meet on unit-price work.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Revision numbering — two upload streams</h4>
              <p style={{ margin: '0 0 .55rem' }}>On the import dialog you choose an <strong>Upload type</strong>, which determines how the upload is numbered:</p>
              <ul style={{ margin: '0 0 .9rem', paddingLeft: '1.2rem' }}>
                <li><strong>Excel from client</strong> — a full MTO snapshot. Each one becomes a new <strong>client revision</strong> (<strong>Rev 0 = Bid</strong>, then Rev 1, 2, 3… = project revisions), tagged with the <strong>Client Revision #</strong> and <strong>Transmittal #</strong> you enter. These are the revisions you compare against one another.</li>
                <li><strong>ISO batch</strong> — isometrics issued in <strong>batches</strong> throughout the job. Each batch is <strong>appended to the bottom</strong> of the list and kept as its own entry (<strong>ISO Batch 1, 2, 3…</strong>, each with its own Transmittal #; the client rev is recorded as <em>"multiple"</em>). If a later batch <strong>re-issues an ISO</strong>, the latest batch wins for that ISO.</li>
              </ul>
              <p style={{ margin: '0 0 .9rem' }}>The <strong>Revision</strong> dropdown lets you view any single client revision, any single ISO batch, <strong>🧩 Consolidated ISOs</strong> (the current scope = latest version of every ISO across all batches), or <strong>All rows</strong>. A PM / lead estimator can <strong>Close bid</strong> on a client revision — a <strong>soft lock</strong>: edits are still possible but discouraged and audit-logged. Revisions/batches can be soft-deleted (data is retained and restorable).</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Comparing &amp; ISO churn</h4>
              <p style={{ margin: '0 0 .9rem' }}>In <strong>📈 Compare revisions</strong>, each <strong>client revision</strong> is one column and <strong>all ISO batches roll up into a single "Consolidated ISOs" column</strong> (latest per ISO) — so the issued-for-construction scope can be measured against the bid and each client rev. The <strong>ISO churn</strong> panel summarises how the ISO batches evolved: how many ISOs were re-issued, how many times, and the net hours those re-issues added — plus a per-ISO history (first → latest qty/hours and the sum across all of its revisions). Each ISO can also carry an <strong>ISO Version</strong> (e.g. 0, 1, A) from the engineering firm; include it under the <em>ISO Version</em> column.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Automatic tracker &amp; change management</h4>
              <p style={{ margin: '0 0 .9rem' }}>Once an estimate is <strong>awarded</strong> to a job, every MTO upload (and the award itself) <strong>automatically rolls budget hours per ISO sheet into the Piping tracker</strong> — non-destructive, so field/shop progress is never erased. The tracker uses a <strong>versioned line model</strong>: each tracker line is tagged with its <strong>ISO Version</strong> and the <strong>MTO Rev #</strong> that produced it (so you can see progress per MTO rev). When a new revision <strong>re-issues an ISO with a new version</strong>, the old tracker line is <strong>kept but struck-through and made read-only</strong> (preserving its baseline progress) and a <strong>new active line is appended</strong> with the new quantity — always, regardless of prior progress, so baseline vs. extra is always distinguishable. Same-version re-uploads simply refresh the budget in place.</p>
              <p style={{ margin: '0 0 .9rem' }}>If a re-issued ISO <strong>already has progress</strong> on site, the new active line is automatically put <strong>ON HOLD</strong>, and the uploader receives a consolidated result email with a hold list and comparison PDF. The system then auto-creates <strong>up to two change orders</strong> in Change Orders Tracking:</p>
              <ul style={{ margin: '0 0 .9rem', paddingLeft: '1.2rem' }}>
                <li><strong>Scope / quantity CO</strong> — documents new ISOs, new ISO versions, and quantity/hour increases vs the previous revision. Budget hours on this CCR are the MTO labour-hour delta, with an AI-written (or templated) justification.</li>
                <li><strong>Earned-hours CO</strong> — a <em>separate</em> CCR that claims the earned hours already progressed on the just-superseded (struck-through) tracker lines. Live earned / EVM / SOV reports exclude superseded lines, so this CCR keeps that already-earned work visible for change-order tracking. It is only created when those superseded lines have earned hours greater than zero.</li>
              </ul>
              <p style={{ margin: '0 0 .9rem' }}>Quantity increases between revisions are flagged on the scope CO the same way. See <strong>📐 Analytics → Change History</strong> for the full audit of detected changes (including <code>earned_hours_claim</code> events linked to the earned-hours CCR).</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Purchasing ↔ Part Rate DB ↔ MTO (the pricing loop)</h4>
              <ol style={{ margin: '0 0 .9rem', paddingLeft: '1.2rem' }}>
                <li><strong>TC — Send to Purchasing:</strong> open <strong>🛒 Send to Purchasing</strong>. Check the <strong>What to send</strong> categories you want (Valves, CS, HDPE, …). The purchaser receives <strong>one Excel file per checked category</strong>. Preview lines also have checkboxes so you can narrow which rows go in those files. The <strong>UNIT</strong> column is the MTO unit of measure (UM; Pipe defaults to FT when UM is blank).</li>
                <li><strong>Purchasing:</strong> fills in the blank <strong>PRICE</strong> column and returns the file(s).</li>
                <li><strong>TC — Import Cost:</strong> in Part Rate DB, use <strong>💲 Import Cost</strong>. Each part’s cost is overwritten and kept in price history. Prices are then <strong>pushed onto matching MTO rows</strong> (Unit Price CAD + material/totals recomputed). If the same part number appears with <strong>more than one different cost</strong> in the file, those parts are <strong>not applied</strong> — you get a list (and CSV) so you can fix the file and re-import.</li>
              </ol>
              <p style={{ margin: '0 0 .9rem' }}>Until a cost is imported for a part, Unit Price CAD stays blank or zero on the MTO. Specs/datasheets can be linked from the send email for purchasing to download.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Analytics overview</h4>
              <p style={{ margin: '0 0 .9rem' }}><strong>📊 Analytics</strong> summarises the loaded MTO: shop/field splits (ratio from <strong>⚙️ Factors</strong>, editable on Overview), <strong>Total ft</strong> for Pipe + T-Pipe + V-Pipe, Pipe-by-Size and Dia-In-by-Joint-Type tables, Joint Type pivot, and Change History. Shop/field ratio for the cards can be saved back to Factors from Analytics.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Rates &amp; blanks</h4>
              <p style={{ margin: 0 }}><strong>Labour rates</strong> (Shop + Field) are set per estimate in <strong>⚙️ Factors</strong> — until both are entered, labour and dependent grand-total columns stay blank. <strong>SCH Modified</strong> is retained for review.</p>
              </>)}

              {helpTab === 'columns' && (
                <>
                  <p style={{ margin: '0 0 .75rem', color: '#6b7280' }}>
                    Every column available in the MTO grid. <strong>Source</strong> shows where each value comes from; <em>Auto-Calculated</em> and <em>Part Rate DB</em> columns are read-only. Choose which columns are visible via <strong>⚙️ Columns</strong>.
                  </p>
                  <div style={{ overflowX: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                    <table style={{ width: '100%', minWidth: '860px', borderCollapse: 'collapse', fontSize: '0.78rem', tableLayout: 'fixed' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151', width: '34px' }}>#</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151', width: '21%' }}>Column</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151', width: '24%' }}>DB Field</th>
                          <th style={{ textAlign: 'center', padding: '6px 8px', color: '#374151', width: '110px' }}>Source</th>
                          <th style={{ textAlign: 'left', padding: '6px 8px', color: '#374151' }}>Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          const SRC = {
                            import: ['Excel Import', '#15803d'],
                            prdb: ['Part Rate DB', '#0d9488'],
                            calc: ['Auto-Calculated', '#9333ea'],
                            manual: ['Editable', '#2563eb'],
                            config: ['Factors Panel', '#d97706'],
                            system: ['System', '#6b7280'],
                          };
                          return COLUMNS.map((col, idx) => {
                            const [srcKey, note] = COLUMN_HELP[col.key] || [col.readOnly ? 'calc' : (col.type === 'select' ? 'manual' : 'import'), ''];
                            const [srcLabel, srcColor] = SRC[srcKey] || SRC.import;
                            return (
                              <tr key={col.key} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                                <td style={{ padding: '5px 8px', color: '#94a3b8' }}>{idx + 1}</td>
                                <td style={{ padding: '5px 8px', fontWeight: 600, color: '#111827', overflowWrap: 'break-word' }}>{col.label}</td>
                                <td style={{ padding: '5px 8px', color: '#6b7280', fontFamily: 'monospace', fontSize: '0.72rem', overflowWrap: 'break-word' }}>{col.key}</td>
                                <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                                  <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '0.68rem', fontWeight: 600, backgroundColor: srcColor, color: 'white', whiteSpace: 'nowrap' }}>{srcLabel}</span>
                                </td>
                                <td style={{ padding: '5px 8px', color: '#374151' }}>{note}</td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Add New Row ── */}
      {isAddingNew && (
        <div style={{ backgroundColor: '#fefce8', padding: '0.75rem 1rem', borderBottom: '1px solid #fde68a', flexShrink: 0 }}>
          <h4 style={{ margin: '0 0 0.5rem 0', color: '#92400e', fontSize: '0.85rem' }}>Add New MTO Record</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: '0.4rem' }}>
            {COLUMNS.slice(0, 12).map(col => (
              <div key={col.key}>
                <label style={{ fontSize: '0.65rem', color: '#92400e', display: 'block', marginBottom: '1px' }}>{col.label}</label>
                {col.type === 'select' ? (
                  <select value={newRow[col.key] || ''} onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))} style={{ width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #fde68a', boxSizing: 'border-box', fontSize: '0.78rem', backgroundColor: 'white' }}>
                    <option value="">--</option>
                    {(col.options || []).map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input type={col.type === 'number' ? 'number' : col.type === 'date' ? 'date' : 'text'} step="0.01" value={newRow[col.key] || ''} onChange={(e) => setNewRow(prev => ({ ...prev, [col.key]: e.target.value }))} style={{ width: '100%', padding: '4px 6px', borderRadius: '4px', border: '1px solid #fde68a', boxSizing: 'border-box', fontSize: '0.78rem', backgroundColor: 'white' }} />
                )}
              </div>
            ))}
          </div>
          <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.4rem' }}>
            <button onClick={handleAddNewRow} style={{ padding: '5px 12px', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem', fontWeight: '500' }}>Save</button>
            <button onClick={() => { setIsAddingNew(false); setNewRow({}); }} style={{ padding: '5px 12px', backgroundColor: '#94a3b8', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.78rem' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* ── Virtualized Data Grid (Material Index pattern) ── */}
      <div style={{ flex: 1, minHeight: 0, overflowX: 'auto', overflowY: 'hidden', borderTop: '1px solid #e2e8f0', backgroundColor: 'white' }}>
        <div style={{ width: 'fit-content', minWidth: totalTableWidth }}>
          <div style={{
            display: 'flex', position: 'sticky', top: 0, zIndex: 10, height: MTO_HEADER_HEIGHT,
            backgroundColor: '#f8fafc', color: '#475569', borderBottom: '2px solid #e2e8f0', fontSize: '0.75rem',
          }}>
            <div style={{
              width: DEL_COL_WIDTH, minWidth: DEL_COL_WIDTH, maxWidth: DEL_COL_WIDTH,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              borderRight: '1px solid #e2e8f0', fontSize: '0.7rem', boxSizing: 'border-box',
            }}>
              <input
                type="checkbox"
                title="Select all (filtered)"
                style={{ accentColor: '#6366f1', cursor: 'pointer' }}
                checked={selectionSummary.allSelected}
                ref={(el) => { if (el) el.indeterminate = selectionSummary.someSelected; }}
                onChange={handleSelectAllFiltered}
              />
              <span>Del</span>
            </div>
            {visibleCols.map((col) => {
              const w = getColWidth(col.key);
              return (
                <div
                  key={col.key}
                  style={{
                    width: w, minWidth: w, maxWidth: w, padding: '7px 4px',
                    display: 'flex', alignItems: 'center',
                    justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start',
                    borderRight: '1px solid #e2e8f0', whiteSpace: 'nowrap', position: 'relative',
                    userSelect: 'none', boxSizing: 'border-box',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: col.type === 'number' ? 'flex-end' : 'flex-start', gap: '3px', minWidth: 0, width: '100%' }}>
                    {col.type === 'factor' ? (
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={`Labour factor "${col.factor?.name}" — shows the multiplier on the rows it applies to`}>{col.label}</span>
                    ) : (<>
                      <span onClick={() => sortHook.handleSort(col.key)} style={{ cursor: 'pointer', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={col.label}>
                        {col.label}
                        {sortHook.sortColumn === col.key && <span style={{ marginLeft: '3px' }}>{sortHook.sortDirection === 'asc' ? '▲' : '▼'}</span>}
                      </span>
                      <span
                        onClick={(e) => { e.stopPropagation(); filterHook.setOpenFilterColumn(filterHook.openFilterColumn === col.key ? null : col.key); }}
                        style={{ cursor: 'pointer', opacity: filterHook.hasColumnFilter(col.key) ? 1 : 0.4, fontSize: '0.65rem', color: filterHook.hasColumnFilter(col.key) ? '#10b981' : '#94a3b8' }}
                        title="Filter"
                      >
                        🔽
                      </span>
                    </>)}
                  </div>
                  {filterHook.openFilterColumn === col.key && (
                    <FilterDropdown
                      column={col}
                      uniqueValues={filterHook.getUniqueValues(col.key)}
                      selectedValues={filterHook.selectedValues[col.key] ?? []}
                      textFilter={filterHook.textFilters[col.key] || null}
                      numericFilter={filterHook.numericFilters[col.key] || null}
                      dateFilter={filterHook.dateFilters[col.key] || null}
                      filterSearch={filterHook.filterSearch}
                      onToggleValue={filterHook.toggleValue}
                      onSelectAll={filterHook.selectAll}
                      onClearColumn={filterHook.clearColumn}
                      onApplyTextFilter={filterHook.applyTextFilter}
                      onApplyNumericFilter={filterHook.applyNumericFilter}
                      onApplyDateFilter={filterHook.applyDateFilter}
                      onClose={() => { filterHook.setOpenFilterColumn(null); filterHook.setFilterSearch(''); }}
                      onSearchChange={filterHook.setFilterSearch}
                    />
                  )}
                  <div
                    onMouseDown={handleResizeStart(col.key, w)}
                    style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '4px', cursor: 'col-resize', backgroundColor: 'transparent' }}
                    onMouseEnter={(e) => { e.target.style.backgroundColor = '#6366f1'; }}
                    onMouseLeave={(e) => { e.target.style.backgroundColor = 'transparent'; }}
                  />
                </div>
              );
            })}
          </div>

          <div style={{ height: tableHeight }}>
            {filteredItems.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#94a3b8' }}>
                {searchTerm ? 'No matching records' : 'No records. Click "Add New" to create one.'}
              </div>
            ) : (
              <List
                key={`mto-${filteredItems.length}-${visibleCols.length}`}
                rowComponent={MtoVirtualRow}
                rowCount={filteredItems.length}
                rowHeight={MTO_ROW_HEIGHT}
                rowProps={rowProps}
                overscanCount={12}
                style={{ height: tableHeight, width: totalTableWidth, overflowX: 'hidden' }}
              />
            )}
          </div>
        </div>
      </div>

      {/* ── Footer ── */}
      <div style={{
        padding: '0.4rem 1rem', backgroundColor: 'white', borderTop: '1px solid #e2e8f0',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.72rem', color: '#94a3b8', flexShrink: 0,
      }}>
        <span>{items.length} items | {footerMeta.typeCount} types | {footerMeta.materialCount} materials | {visibleColumns.size} columns</span>
        <span>Qty: {summaryStats.totalQty.toLocaleString()} | Cost: {formatCurrency(summaryStats.totalCAD)} | Hrs: {summaryStats.totalHR.toLocaleString()} | Joints: {summaryStats.totalJoints.toLocaleString()}</span>
      </div>

      {/* ── Modals ── */}
      <GridExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} onExport={handleExport} isExporting={mtoExporting} title="Export MTO Data" accentColor="#6366f1" visibleCount={visibleColumns.size} totalCount={COLUMNS.length} />
      <UploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} onUploadSuccess={handleUploadSuccess} onUploadError={handleUploadError} config={UPLOAD_CONFIGS.MTO} jobNumber={jobNumber} estimateNumber={estimateNumber} />
      {showAnalytics && <MtoAnalyticsDashboard data={items} onClose={() => setShowAnalytics(false)} estimateNumber={estimateNumber} jobNumber={jobNumber} />}
      <AuditLogModal isOpen={showAuditLog} onClose={() => setShowAuditLog(false)} endpoint="/mto/audit-log" title="MTO Audit Log" accentColor="#6366f1" />
      <MtoFactorsModal isOpen={showFactors} onClose={() => setShowFactors(false)} estimateNumber={estimateNumber} jobNumber={jobNumber} onApplied={() => { fetchItems(); fetchEstFactors(); showToast('Factors applied — totals recomputed', 'success'); }} />
      <MtoCompareModal isOpen={showCompare} onClose={() => setShowCompare(false)} estimateNumber={estimateNumber} revisions={revisions} />

      <SendToPurchasingModal
        isOpen={showPurchasing}
        onClose={closePurchasing}
        estimateNumber={estimateNumber}
        jobNumber={jobNumber}
        selectedRev={selectedRev}
        selectedIds={selectedIds}
        filteredItemIds={filteredItemIds}
        onSent={fetchItems}
        showToast={showToast}
      />

    </div>
  );
};

export default MtoLog;
